import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";

// Icons
import { 
  Headphones, Globe, Play, Pause, SkipBack, SkipForward, 
  Volume2, Library, LogIn, LogOut, Menu, X, ChevronRight,
  Loader2, Check, AlertCircle, Bookmark, Trash2, Upload,
  Languages, Mic, Shield, Clock, User, Sparkles, Users,
  GraduationCap, Radio, Brain, Building2, CheckCircle2,
  ChevronDown, Info, Download, Zap, Crown, Star, CreditCard,
  Timer, Gauge, RefreshCw, Settings, LayoutDashboard, Server,
  ClipboardList, BarChart3, Home, Activity, TrendingUp, Eye,
  Search, FileAudio
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ==================== REUSABLE COMPONENTS ====================

// Audio Wave Bars
const AudioBars = ({ playing = true, className = "" }) => {
  const heights = [15, 22, 18, 26, 20]; // Fixed heights instead of random
  return (
    <div className={`audio-bars ${className}`}>
      {heights.map((h, i) => (
        <span 
          key={i} 
          style={{ 
            height: `${h}px`,
            animationPlayState: playing ? 'running' : 'paused' 
          }} 
        />
      ))}
    </div>
  );
};

// Morphing Shape Background
const MorphShape = ({ className = "", delay = 0, variant = "cyan" }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.5, delay }}
    className={`absolute morph-float ${className}`}
    style={{
      background: variant === "cyan" 
        ? 'linear-gradient(135deg, rgba(61, 208, 214, 0.15), rgba(168, 213, 186, 0.1))'
        : 'linear-gradient(135deg, rgba(242, 197, 124, 0.12), rgba(61, 208, 214, 0.08))',
      filter: 'blur(60px)',
    }}
  />
);

// Language Pill
const LanguagePill = ({ children, delay = 0 }) => (
  <motion.span
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    className="lang-pill px-4 py-2 rounded-full text-sm font-medium"
  >
    {children}
  </motion.span>
);

// Floating Particle - cyan/gold morph
const Particle = ({ style, delay = 0, variant = "cyan" }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 0.6 }}
    transition={{ duration: 2, delay }}
    className={`particle absolute w-2 h-2 rounded-full ${variant === "gold" ? "particle-gold" : "particle-cyan"}`}
    style={{ ...style, animationDelay: `${delay}s` }}
  />
);

// Tooltip Component
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block">
      <div 
        onMouseEnter={() => setShow(true)} 
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="tooltip absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap z-50"
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== NAVBAR ====================
const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
      className={`fixed top-6 left-1/2 -translate-x-1/2 w-[90%] max-w-4xl h-14 rounded-full z-50 flex items-center justify-between px-6 transition-all duration-500 ${
        isScrolled ? "glass-heavy" : "glass"
      }`}
    >
      <div 
        className="flex items-center gap-2 cursor-pointer" 
        onClick={() => navigate("/")}
        data-testid="navbar-logo"
      >
        <img 
          src="/audiogola-logo.svg" 
          alt="AUDIOGOLA" 
          className="h-8 w-8"
        />
        <span className="font-semibold text-white text-lg tracking-wide">AUDIOGOLA</span>
      </div>

      <div className="hidden md:flex items-center gap-4">
        <button 
          onClick={() => navigate("/pricing")} 
          className="transition-colors text-sm flex items-center gap-2 hover:text-cyan-400"
          style={{ color: '#7B8A9C' }}
          data-testid="nav-pricing"
        >
          Pricing
        </button>
        {user && (
          <button 
            onClick={() => navigate("/library")} 
            className="transition-colors text-sm flex items-center gap-2"
            style={{ color: '#7B8A9C' }}
            data-testid="nav-library"
          >
            <Library className="w-4 h-4" />
            Library
          </button>
        )}
        {user?.admin_role && (
          <button 
            onClick={() => navigate("/admin")} 
            className="transition-colors text-sm flex items-center gap-2 text-amber-400 hover:text-amber-300"
            data-testid="nav-admin"
          >
            <Settings className="w-4 h-4" />
            Admin
          </button>
        )}
        {user ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
              ) : (
                <User className="w-7 h-7 p-1 rounded-full" style={{ background: 'rgba(31,42,68,0.1)' }} />
              )}
            </div>
            <button
              onClick={onLogout}
              className="btn-secondary px-4 py-2 rounded-full text-sm"
              data-testid="logout-btn"
            >
              Logout
            </button>
          </div>
        ) : (
          <button
            onClick={handleLogin}
            className="btn-primary px-5 py-2 rounded-full text-sm flex items-center gap-2"
            data-testid="login-btn"
          >
            <LogIn className="w-4 h-4" />
            Sign In
          </button>
        )}
      </div>

      {/* Mobile Menu Button - positioned within navbar */}
      <button 
        className="md:hidden p-2 text-white/60 flex-shrink-0" 
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-16 left-0 right-0 glass-heavy rounded-2xl p-5 md:hidden"
          >
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => { navigate("/pricing"); setMobileMenuOpen(false); }} 
                className="text-left py-2 text-white/70"
              >
                Pricing
              </button>
              {user && (
                <button 
                  onClick={() => { navigate("/library"); setMobileMenuOpen(false); }} 
                  className="text-left py-2 text-white/70"
                >
                  Library
                </button>
              )}
              {user ? (
                <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="btn-secondary px-4 py-2 rounded-full text-sm">
                  Logout
                </button>
              ) : (
                <button onClick={() => { handleLogin(); setMobileMenuOpen(false); }} className="btn-primary px-5 py-2 rounded-full text-sm">
                  Sign In
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// ==================== LANDING PAGE ====================
const LandingPage = ({ user }) => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [language, setLanguage] = useState("");
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState("");
  const [previewOnly, setPreviewOnly] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [elevenLabsSettings, setElevenLabsSettings] = useState(null);
  const [showElevenLabsModal, setShowElevenLabsModal] = useState(false);
  const [elevenLabsVoiceId, setElevenLabsVoiceId] = useState("");
  const [elevenLabsApiKey, setElevenLabsApiKey] = useState("");
  const [savingElevenLabs, setSavingElevenLabs] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [debugLog, setDebugLog] = useState([]); // Debug log for mobile
  // Video translation states
  const [translationType, setTranslationType] = useState(null); // 'audio' or 'video'
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoSessionId, setVideoSessionId] = useState(null);
  const [videoProgress, setVideoProgress] = useState(null);
  const fileInputRef = useRef(null);
  const { scrollYProgress } = useScroll();
  
  // Debug logger for mobile devices
  const addDebug = (msg) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLog(prev => [...prev.slice(-4), `${timestamp}: ${msg}`]);
    console.log(`[AUDIOGOLA] ${msg}`);
  };
  
  // Check if user has already accepted terms (registered users who accepted during signup)
  const userHasAcceptedTerms = user?.terms_accepted === true;
  
  // Voice options for TTS
  const voiceOptions = [
    { id: 'nova', name: 'Nova', description: 'Female, warm', gender: 'female' },
    { id: 'shimmer', name: 'Shimmer', description: 'Female, clear', gender: 'female' },
    { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced', gender: 'neutral' },
    { id: 'echo', name: 'Echo', description: 'Male, warm', gender: 'male' },
    { id: 'fable', name: 'Fable', description: 'Male, expressive', gender: 'male' },
    { id: 'onyx', name: 'Onyx', description: 'Male, deep', gender: 'male' },
  ];

  // Fetch subscription, ElevenLabs settings, and terms content on mount
  useEffect(() => {
    // Fetch terms content
    axios.get(`${API}/terms`)
      .then(res => setTermsContent(res.data.content))
      .catch(() => {});
    
    if (user) {
      // Fetch subscription
      axios.get(`${API}/subscription`, { withCredentials: true })
        .then(res => setSubscription(res.data))
        .catch(() => {});
      
      // Fetch ElevenLabs settings
      axios.get(`${API}/settings/elevenlabs`, { withCredentials: true })
        .then(res => {
          if (res.data.configured) {
            setElevenLabsSettings(res.data);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const handleSaveElevenLabs = async () => {
    if (!elevenLabsVoiceId || !elevenLabsApiKey) {
      toast.error("Please enter both Voice ID and API Key");
      return;
    }
    
    setSavingElevenLabs(true);
    try {
      const res = await axios.post(`${API}/settings/elevenlabs`, {
        voice_id: elevenLabsVoiceId,
        api_key: elevenLabsApiKey
      }, { withCredentials: true });
      
      setElevenLabsSettings({
        configured: true,
        voice_id: elevenLabsVoiceId,
        voice_name: res.data.voice_name,
        has_api_key: true
      });
      setShowElevenLabsModal(false);
      setElevenLabsApiKey(""); // Clear sensitive data
      toast.success(`Voice "${res.data.voice_name}" configured successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save ElevenLabs settings");
    } finally {
      setSavingElevenLabs(false);
    }
  };

  const handleRemoveElevenLabs = async () => {
    try {
      await axios.delete(`${API}/settings/elevenlabs`, { withCredentials: true });
      setElevenLabsSettings(null);
      setSelectedVoice("nova");
      toast.success("Custom voice removed");
    } catch (error) {
      toast.error("Failed to remove custom voice");
    }
  };

  useEffect(() => {
    axios.get(`${API}/languages`)
      .then(res => setLanguages(res.data.languages || []))
      .catch(err => {
        console.error("Failed to load languages:", err);
        // Fallback languages if API fails
        setLanguages([
          { code: "hinglish", name: "Hinglish (Hindi + English)" },
          { code: "hi", name: "Hindi" },
          { code: "en-IN", name: "English (Indian)" },
          { code: "ta", name: "Tamil" },
          { code: "te", name: "Telugu" },
          { code: "en", name: "English (US)" },
          { code: "es", name: "Spanish" },
          { code: "fr", name: "French" }
        ]);
      });
  }, []);

  // Check if file is a video
  const isVideoFile = (file) => {
    const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.wmv', '.flv'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    return videoExtensions.includes(ext) || file.type.startsWith('video/');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Check if video file - show type selection
      if (isVideoFile(file)) {
        setShowTypeSelection(true);
        setTranslationType(null);
      } else {
        // Audio file - go directly to audio translation
        setTranslationType('audio');
        setShowTypeSelection(false);
        setShowUploadModal(true);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (isVideoFile(file)) {
        setShowTypeSelection(true);
        setTranslationType(null);
      } else {
        setTranslationType('audio');
        setShowTypeSelection(false);
        setShowUploadModal(true);
      }
    }
  };

  const handleTypeSelect = (type) => {
    setTranslationType(type);
    setShowTypeSelection(false);
    setShowUploadModal(true);
  };

  // Video translation submit handler
  const handleVideoSubmit = async () => {
    const termsRequired = !userHasAcceptedTerms;
    
    if (!selectedFile || !language) {
      toast.error("Please select a file and language");
      return;
    }
    
    if (termsRequired && !agreedToTerms) {
      toast.error("Please agree to the Terms and Conditions");
      return;
    }

    setIsLoading(true);
    addDebug(`Starting video upload: ${selectedFile?.name}`);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('target_language', language);
      formData.append('voice', selectedVoice);
      
      const res = await axios.post(`${API}/video/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minute timeout for large video files
      });
      
      setVideoJobId(res.data.job_id);
      setVideoSessionId(res.data.session_id);
      toast.success("Video uploaded! Translation in progress...");
      
      // Navigate to video player page with job info
      navigate(`/video/${res.data.job_id}`, { 
        state: { 
          sessionId: res.data.session_id,
          estimatedChunks: res.data.estimated_chunks,
          duration: res.data.video_duration_seconds
        } 
      });
      
    } catch (error) {
      console.error('Video upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      toast.error(errorMsg);
      addDebug(`Video upload error: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    // If video translation selected, use video handler
    if (translationType === 'video') {
      return handleVideoSubmit();
    }
    
    // Registered users who accepted terms don't need checkbox
    // Guest users and registered users who haven't accepted need checkbox
    const termsRequired = !userHasAcceptedTerms;
    
    if (!selectedFile || !language) {
      toast.error("Please select a file and language");
      return;
    }
    
    if (termsRequired && !agreedToTerms) {
      toast.error("Please agree to the Terms and Conditions");
      return;
    }

    setIsLoading(true);
    addDebug(`Starting upload: ${selectedFile?.name} (${(selectedFile?.size/1024/1024).toFixed(2)}MB)`);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('target_language', language);
      formData.append('preview_only', previewOnly);
      formData.append('voice', selectedVoice);
      
      addDebug(`Sending to ${API}/translate/upload...`);
      
      const res = await axios.post(`${API}/translate/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minute timeout for large files
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (percentCompleted % 25 === 0) { // Log at 25%, 50%, 75%, 100%
            addDebug(`Upload progress: ${percentCompleted}%`);
          }
        }
      });
      
      addDebug(`Success! Job ID: ${res.data.job_id}`);
      setShowUploadModal(false);
      navigate(`/player/${res.data.job_id}`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      addDebug(`ERROR: ${error.code || 'UNKNOWN'} - ${errorDetail}`);
      
      // More specific error messages
      let errorMsg = "Failed to start translation";
      if (error.code === 'ECONNABORTED') {
        errorMsg = "Upload timed out. Please try with a smaller file or check your connection.";
      } else if (error.code === 'ERR_NETWORK') {
        errorMsg = "Network error. Please check your internet connection.";
      } else if (error.response?.status === 413) {
        errorMsg = "File too large. Please try a smaller file.";
      } else if (error.response?.status === 403) {
        errorMsg = error.response?.data?.detail || "Access denied. Please check your usage limits.";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTryPreview = () => {
    setPreviewOnly(true);
    fileInputRef.current?.click();
  };

  // Parallax values
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <div className="min-h-screen noise hero-gradient">
      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden ambient-glow">
        {/* Background morphing shapes */}
        <MorphShape className="w-[600px] h-[600px] -top-20 -right-40" delay={0} variant="cyan" />
        <MorphShape className="w-[500px] h-[500px] -bottom-20 -left-40" delay={0.5} variant="gold" />
        
        {/* Floating particles - mix of cyan and gold */}
        {[...Array(12)].map((_, i) => (
          <Particle 
            key={i} 
            delay={i * 0.3}
            variant={i % 2 === 0 ? "cyan" : "gold"}
            style={{ 
              top: `${20 + Math.random() * 60}%`, 
              left: `${10 + Math.random() * 80}%` 
            }} 
          />
        ))}

        <motion.div 
          style={{ y: heroY, opacity: heroOpacity }}
          className="max-w-4xl mx-auto text-center relative z-10 pt-32 pb-20"
        >
          {/* AUDIOGOLA Logo - Clean */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative mx-auto mb-12 flex items-center justify-center"
          >
            {/* Logo image */}
            <img 
              src="/audiogola-logo.svg" 
              alt="AUDIOGOLA" 
              className="h-36 md:h-48 w-auto"
            />
            {/* Language tags floating around logo */}
            <motion.div
              animate={{ y: [-5, 5, -5], x: [-3, 3, -3] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 right-0 md:right-4"
            >
              <LanguagePill delay={0.8}>Hindi</LanguagePill>
            </motion.div>
            <motion.div
              animate={{ y: [5, -5, 5], x: [3, -3, 3] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-4 left-0 md:left-4"
            >
              <LanguagePill delay={1}>Spanish</LanguagePill>
            </motion.div>
            <motion.div
              animate={{ y: [-3, 7, -3] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-1/2 -right-8 md:-right-20"
            >
              <LanguagePill delay={1.2}>French</LanguagePill>
            </motion.div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-5xl md:text-7xl font-bold leading-[1.1] mb-6"
          >
            <span style={{ color: '#3DD0D6' }}>One file.</span>{" "}
            <span style={{ color: '#F8FAFC' }}>Many voices.</span>
            <br />
            <span style={{ color: '#A8D5BA' }}>Endless reach.</span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-xl md:text-2xl mb-12 font-light"
            style={{ color: 'rgba(248, 250, 252, 0.5)' }}
          >
            Upload your audio. Hear it in every language.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Tooltip content="Upload videos you own or have rights to.">
              <label 
                className="btn-primary px-8 py-4 rounded-full text-lg flex items-center gap-3 cursor-pointer"
                data-testid="upload-audio-btn"
              >
                <Upload className="w-5 h-5" />
                Upload Audio/Video
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="audio/*,video/*,.mp3,.wav,.m4a,.mp4,.mov,.avi,.webm,.3gp,.ogg,.aac"
                  className="hidden"
                  data-testid="hero-file-input"
                />
              </label>
            </Tooltip>
            
            <button
              onClick={handleTryPreview}
              className="btn-secondary px-8 py-4 rounded-full text-lg flex items-center gap-3"
              data-testid="try-preview-btn"
            >
              <Play className="w-5 h-5" />
              Try Free Preview
            </button>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-white/30"
            >
              <ChevronDown className="w-6 h-6" />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="py-32 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#F8FAFC' }}>
              Turn one file into many languages —{" "}
              <span style={{ color: '#F2C57C' }}>effortlessly.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-px step-line" />

            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-6 glass-card rounded-2xl flex items-center justify-center morph-card">
                <Upload className="w-8 h-8" style={{ color: '#F2C57C' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#F8FAFC' }}>Upload</h3>
              <p className="leading-relaxed" style={{ color: 'rgba(248, 250, 252, 0.5)' }}>
                Add your audio or video file in one click.
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-6 glass-card rounded-2xl flex items-center justify-center morph-card">
                <Languages className="w-8 h-8" style={{ color: '#3DD0D6' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#F8FAFC' }}>Choose Languages</h3>
              <p className="leading-relaxed" style={{ color: 'rgba(248, 250, 252, 0.5)' }}>
                Select the languages you want. Preview instantly.
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center"
            >
              <div className="w-20 h-20 mx-auto mb-6 glass-card rounded-2xl flex items-center justify-center morph-card">
                <Headphones className="w-8 h-8" style={{ color: '#A8D5BA' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#F8FAFC' }}>Listen & Export</h3>
              <p className="leading-relaxed" style={{ color: 'rgba(248, 250, 252, 0.5)' }}>
                Download translated audio or share directly.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== WHO THIS IS FOR ==================== */}
      <section className="py-32 px-6 relative ambient-glow">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
              Perfect for anyone working with{" "}
              <span style={{ color: '#3DD0D6' }}>audio.</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Radio, title: "Podcasters & Educators", delay: 0.1 },
              { icon: GraduationCap, title: "Students & Researchers", delay: 0.2 },
              { icon: Globe, title: "Global Audiences", delay: 0.3 },
              { icon: Building2, title: "Teams & Agencies", delay: 0.4 },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: item.delay }}
                className="glass-card rounded-2xl p-6 morph-card text-center card-warm-hover"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center" style={{ background: 'rgba(61, 208, 214, 0.1)' }}>
                  <item.icon className="w-7 h-7" style={{ color: '#3DD0D6' }} />
                </div>
                <h3 className="font-medium" style={{ color: '#F8FAFC' }}>{item.title}</h3>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-center text-sm mt-10"
            style={{ color: 'rgba(248, 250, 252, 0.4)' }}
          >
            Users are responsible for uploading only audio they are authorized to use.
          </motion.p>
        </div>
      </section>

      {/* ==================== FREE VS PAID ==================== */}
      <section className="py-32 px-6 section-glass">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
              Try before you commit.{" "}
              <span style={{ color: '#F2C57C' }}>Scale when ready.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Free */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card rounded-3xl p-8 pricing-free"
            >
              <h3 className="text-2xl font-semibold mb-2" style={{ color: '#F8FAFC' }}>Free</h3>
              <p className="text-3xl font-bold text-white mb-2">20 min<span className="text-base font-normal text-white/50">/month</span></p>
              <p className="mb-6" style={{ color: 'rgba(248, 250, 252, 0.5)' }}>Get started instantly</p>
              <ul className="space-y-4">
                {[
                  "5 min max per audio",
                  "Major languages",
                  "Standard voices",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3" style={{ color: 'rgba(248, 250, 252, 0.6)' }}>
                    <Check className="w-5 h-5" style={{ color: '#A8D5BA' }} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Paid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="glass-card rounded-3xl p-8 pricing-paid relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 px-4 py-1 text-xs font-medium rounded-bl-xl" style={{ background: 'rgba(61, 208, 214, 0.15)', color: '#3DD0D6' }}>
                POPULAR
              </div>
              <h3 className="text-2xl font-semibold mb-2" style={{ color: '#F2C57C' }}>Pro</h3>
              <p className="text-3xl font-bold text-white mb-2">$10<span className="text-base font-normal text-white/50">/month</span></p>
              <p className="mb-6" style={{ color: 'rgba(248, 250, 252, 0.5)' }}>220 min • 30 min max per audio</p>
              <ul className="space-y-4">
                {[
                  "Expanded languages",
                  "Premium voices",
                  "Voice cloning (1 voice)",
                  "Faster processing",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3" style={{ color: '#F8FAFC' }}>
                    <CheckCircle2 className="w-5 h-5" style={{ color: '#3DD0D6' }} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
          
          {/* View All Plans CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-10"
          >
            <button
              onClick={() => navigate("/pricing")}
              className="btn-secondary px-8 py-3 rounded-full inline-flex items-center gap-2"
              data-testid="view-all-plans-btn"
            >
              View All Plans
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ==================== TRUST & COMPLIANCE ==================== */}
      <section className="py-32 px-6 relative">
        <MorphShape className="w-[400px] h-[400px] top-0 right-0 opacity-50" variant="cyan" />
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-20 h-20 mx-auto mb-8 glass-card rounded-2xl flex items-center justify-center shield-glow">
              <Shield className="w-10 h-10" style={{ color: '#A8D5BA' }} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#F8FAFC' }}>
              Your content.{" "}
              <span style={{ color: '#3DD0D6' }}>Your control.</span>
            </h2>
            <p className="text-xl leading-relaxed max-w-xl mx-auto" style={{ color: 'rgba(248, 250, 252, 0.5)' }}>
              We process only files you upload. We do not scrape or access third-party platforms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-32 px-6 relative overflow-hidden">
        <MorphShape className="w-[600px] h-[600px] -bottom-40 left-1/2 -translate-x-1/2" />
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto text-center relative z-10"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: '#F8FAFC' }}>
            Understanding should flow —{" "}
            <span style={{ color: '#3DD0D6' }}>like sound itself.</span>
          </h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <label className="btn-primary px-10 py-5 rounded-full text-lg inline-flex items-center gap-3 cursor-pointer">
              <Headphones className="w-6 h-6" />
              Start Listening in Your Language
              <input
                type="file"
                onChange={handleFileSelect}
                accept=".mp3,.wav,.m4a,.mp4,audio/*"
                className="hidden"
                data-testid="cta-file-input"
              />
            </label>
          </motion.div>
        </motion.div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="py-8 px-6 border-t border-white/10 bg-slate-900/50">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="/audiogola-logo.svg" 
              alt="AUDIOGOLA" 
              className="h-8 w-auto"
            />
            <span className="font-semibold text-white">AUDIOGOLA</span>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/terms")}
              className="text-sm text-white/60 hover:text-cyan-400 transition-colors"
              data-testid="footer-terms-link"
            >
              Terms & Conditions
            </button>
            <span className="text-white/20">|</span>
            <button 
              onClick={() => navigate("/pricing")}
              className="text-sm text-white/60 hover:text-cyan-400 transition-colors"
            >
              Pricing
            </button>
          </div>
          <div className="text-center md:text-right">
            <p className="text-sm text-white/60">
              © 2025 AUDIOGOLA. All Rights Reserved.
            </p>
            <a 
              href="https://www.audiogola.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              www.audiogola.com
            </a>
          </div>
        </div>
      </footer>

      {/* ==================== TYPE SELECTION MODAL ==================== */}
      <AnimatePresence>
        {showTypeSelection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => {
              setShowTypeSelection(false);
              setSelectedFile(null);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="glass-heavy rounded-3xl p-8 max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="type-selection-modal"
            >
              <h3 className="text-2xl font-semibold mb-2">What would you like to translate?</h3>
              <p className="text-white/60 text-sm mb-6">
                You&apos;ve uploaded a video file. Choose your translation type:
              </p>
              
              {/* File info */}
              <div className="glass-card rounded-xl p-4 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-400/10 flex items-center justify-center">
                  <FileAudio className="w-6 h-6 text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{selectedFile?.name}</p>
                  <p className="text-sm text-white/40">
                    {selectedFile && (selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              
              {/* Options */}
              <div className="space-y-4">
                <button
                  onClick={() => handleTypeSelect('audio')}
                  className="w-full p-5 rounded-xl border-2 border-white/10 hover:border-cyan-400/50 transition-all text-left group"
                  style={{ backgroundColor: '#0f172a' }}
                  data-testid="select-audio-translation"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-400/10 flex items-center justify-center group-hover:bg-cyan-400/20 transition-colors">
                      <Headphones className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">Translate Audio Only</h4>
                      <p className="text-sm text-white/60">Extract and translate the audio track only</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => handleTypeSelect('video')}
                  className="w-full p-5 rounded-xl border-2 border-cyan-400/30 hover:border-cyan-400 transition-all text-left group relative overflow-hidden"
                  style={{ backgroundColor: '#0f172a' }}
                  data-testid="select-video-translation"
                >
                  <div className="absolute top-2 right-2 px-2 py-0.5 bg-cyan-400/20 rounded-full text-xs text-cyan-400">
                    NEW
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-400/10 flex items-center justify-center group-hover:bg-cyan-400/20 transition-colors">
                      <Play className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">Translate Full Video</h4>
                      <p className="text-sm text-white/60">Replace audio with translated voice (keeps video)</p>
                    </div>
                  </div>
                </button>
              </div>
              
              <button
                onClick={() => {
                  setShowTypeSelection(false);
                  setSelectedFile(null);
                }}
                className="w-full mt-6 py-3 text-white/60 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== UPLOAD MODAL ==================== */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="glass-heavy rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="upload-modal"
            >
              <h3 className="text-2xl font-semibold mb-6">
                {translationType === 'video' ? 'Translate Your Video' : 'Translate Your Audio'}
              </h3>
              
              {/* Video translation notice */}
              {translationType === 'video' && (
                <div className="mb-4 p-3 rounded-xl bg-cyan-400/10 border border-cyan-400/20">
                  <p className="text-sm text-cyan-400 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Video translation may take several minutes depending on length.
                  </p>
                </div>
              )}
              
              {/* File info */}
              <div className="glass-card rounded-xl p-4 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-200/10 flex items-center justify-center">
                  {translationType === 'video' ? (
                    <Play className="w-6 h-6 text-cyan-400" />
                  ) : (
                    <Headphones className="w-6 h-6 text-amber-200/80" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{selectedFile?.name}</p>
                  <p className="text-sm text-white/40">
                    {selectedFile && (selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              
              {/* Rights disclaimer */}
              <p className="text-xs text-white/40 mb-6 flex items-center gap-2">
                <Info className="w-3 h-3" />
                By uploading, you confirm you have rights to use this content.
              </p>

              {/* Language selector - Custom dropdown for iOS compatibility */}
              <div className="mb-6">
                <label className="block text-sm text-white/60 mb-2">Target Language</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                    className="w-full h-12 px-4 rounded-xl border border-white/10 text-white text-left flex items-center justify-between"
                    style={{ backgroundColor: '#0f172a' }}
                    data-testid="modal-language-select"
                  >
                    <span style={{ color: language ? '#ffffff' : '#94a3b8' }}>
                      {language || "Select language"}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} style={{ color: '#ffffff' }} />
                  </button>
                  
                  {showLanguageDropdown && (
                    <div 
                      className="mt-2 rounded-xl border border-white/10 overflow-hidden max-h-48 overflow-y-auto"
                      style={{ backgroundColor: '#0f172a' }}
                      data-testid="language-dropdown-list"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => {
                            setLanguage(lang.name);
                            setShowLanguageDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-slate-800 flex items-center justify-between"
                          style={{ backgroundColor: language === lang.name ? '#1e293b' : '#0f172a', color: '#ffffff' }}
                          data-testid={`language-option-${lang.code}`}
                        >
                          <span>{lang.name}</span>
                          {language === lang.name && <Check className="w-4 h-4" style={{ color: '#3DD0D6' }} />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Voice selector */}
              <div className="mb-6">
                <label className="block text-sm text-white/60 mb-3">Voice</label>
                
                {/* My Voice option - only show if logged in */}
                {user && (
                  <div className="mb-3">
                    {!subscription?.voice_cloning_enabled ? (
                      // User doesn't have voice cloning access
                      <div 
                        className="w-full p-4 rounded-xl text-left border-2 border-dashed border-white/10 glass-card cursor-pointer"
                        onClick={() => navigate("/pricing")}
                        data-testid="voice-cloning-upgrade"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                            <Mic className="w-5 h-5 text-white/30" />
                          </div>
                          <div className="flex-1">
                            <span className="font-medium text-white/40">My Voice</span>
                            <p className="text-xs text-white/30">Requires Pro plan or higher</p>
                          </div>
                          <span className="px-2 py-1 bg-amber-500/10 text-amber-400 text-xs rounded-full">Upgrade</span>
                        </div>
                      </div>
                    ) : elevenLabsSettings?.configured ? (
                      <button
                        onClick={() => setSelectedVoice("my_voice")}
                        className={`w-full p-4 rounded-xl text-left transition-all border-2 ${
                          selectedVoice === "my_voice"
                            ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-400 shadow-lg shadow-purple-500/20'
                            : 'glass-card border-transparent hover:border-purple-400/50'
                        }`}
                        data-testid="voice-my_voice"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                              <Mic className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <span className={`font-semibold ${selectedVoice === "my_voice" ? 'text-purple-300' : 'text-white'}`}>
                                My Voice
                              </span>
                              <p className="text-xs text-white/50">{elevenLabsSettings.voice_name} via ElevenLabs</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedVoice === "my_voice" && <Check className="w-4 h-4 text-purple-400" />}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowElevenLabsModal(true);
                                setElevenLabsVoiceId(elevenLabsSettings.voice_id);
                              }}
                              className="text-xs text-white/40 hover:text-white/60 px-2 py-1"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowElevenLabsModal(true)}
                        className="w-full p-4 rounded-xl text-left transition-all border-2 border-dashed border-white/20 hover:border-purple-400/50 glass-card group"
                        data-testid="setup-my-voice"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/5 group-hover:bg-purple-500/20 flex items-center justify-center transition-colors">
                            <Mic className="w-5 h-5 text-white/40 group-hover:text-purple-400" />
                          </div>
                          <div>
                            <span className="font-medium text-white/60 group-hover:text-purple-300">Use My Voice</span>
                            <p className="text-xs text-white/40">Clone your voice with ElevenLabs</p>
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-2">
                  {voiceOptions.map((voice) => (
                    <button
                      key={voice.id}
                      onClick={() => setSelectedVoice(voice.id)}
                      className={`p-3 rounded-xl text-left transition-all border-2 ${
                        selectedVoice === voice.id 
                          ? 'bg-cyan-500/20 border-cyan-400 shadow-lg shadow-cyan-500/20' 
                          : 'glass-card border-transparent hover:border-white/20'
                      }`}
                      data-testid={`voice-${voice.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${
                          voice.gender === 'female' ? 'bg-pink-400' : 
                          voice.gender === 'male' ? 'bg-blue-400' : 'bg-gray-400'
                        }`} />
                        <span className={`font-medium text-sm ${selectedVoice === voice.id ? 'text-cyan-300' : ''}`}>{voice.name}</span>
                        {selectedVoice === voice.id && <Check className="w-3 h-3 text-cyan-400 ml-auto" />}
                      </div>
                      <p className={`text-xs ${selectedVoice === voice.id ? 'text-white/60' : 'text-white/40'}`}>{voice.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview toggle */}
              <label className="flex items-center gap-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={previewOnly}
                  onChange={(e) => setPreviewOnly(e.target.checked)}
                  className="w-5 h-5 rounded"
                  data-testid="preview-toggle"
                />
                <span className="text-white/70">Preview only (30 seconds)</span>
              </label>

              {/* Terms checkbox - Only show for guests or users who haven't accepted */}
              {!userHasAcceptedTerms && (
                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="w-5 h-5 rounded mt-0.5 accent-cyan-500"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm leading-relaxed" style={{ color: '#7B8A9C' }}>
                      I confirm I have the right to process this audio and agree to the{' '}
                      <button 
                        type="button"
                        onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                        className="text-cyan-400 hover:text-cyan-300 underline"
                        data-testid="view-terms-link"
                      >
                        Terms & Conditions
                      </button>
                    </span>
                  </label>
                </div>
              )}

              {/* Debug Log - visible on mobile for troubleshooting */}
              {debugLog.length > 0 && (
                <div className="mb-4 p-3 rounded-xl bg-black/40 border border-white/10">
                  <p className="text-xs text-amber-200/60 mb-2 font-mono">Debug Log:</p>
                  {debugLog.map((log, i) => (
                    <p key={i} className="text-xs text-white/50 font-mono truncate">{log}</p>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 btn-secondary py-3 rounded-full"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !language || (!userHasAcceptedTerms && !agreedToTerms)}
                  className="flex-1 btn-primary py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="start-translation-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Translate
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ElevenLabs Settings Modal */}
      <AnimatePresence>
        {showElevenLabsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowElevenLabsModal(false)}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md glass-card rounded-2xl p-6 shadow-2xl"
              data-testid="elevenlabs-modal"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">My Voice Setup</h2>
                  <p className="text-sm text-white/50">Use your ElevenLabs cloned voice</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-white/60 mb-2">Voice ID</label>
                  <input
                    type="text"
                    value={elevenLabsVoiceId}
                    onChange={(e) => setElevenLabsVoiceId(e.target.value)}
                    placeholder="e.g., pNInz6obpgDQGcFmaJgB"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-purple-400 focus:outline-none"
                    data-testid="elevenlabs-voice-id-input"
                  />
                  <p className="text-xs text-white/40 mt-1">
                    Find this in your ElevenLabs dashboard → Voice Library
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-white/60 mb-2">API Key</label>
                  <input
                    type="password"
                    value={elevenLabsApiKey}
                    onChange={(e) => setElevenLabsApiKey(e.target.value)}
                    placeholder="Enter your ElevenLabs API key"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-purple-400 focus:outline-none"
                    data-testid="elevenlabs-api-key-input"
                  />
                  <p className="text-xs text-white/40 mt-1">
                    Get your API key from{" "}
                    <a 
                      href="https://elevenlabs.io/app/settings/api-keys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-purple-400 hover:underline"
                    >
                      elevenlabs.io/app/settings/api-keys
                    </a>
                  </p>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-medium text-white/80 mb-2">How to get your Voice ID:</h4>
                <ol className="text-xs text-white/50 space-y-1 list-decimal list-inside">
                  <li>Go to ElevenLabs and create/clone your voice</li>
                  <li>Open your voice in the Voice Library</li>
                  <li>Copy the Voice ID from the voice settings</li>
                </ol>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowElevenLabsModal(false);
                    setElevenLabsApiKey("");
                  }}
                  className="flex-1 btn-secondary py-3 rounded-full"
                >
                  Cancel
                </button>
                {elevenLabsSettings?.configured && (
                  <button
                    onClick={() => {
                      handleRemoveElevenLabs();
                      setShowElevenLabsModal(false);
                    }}
                    className="px-4 py-3 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={handleSaveElevenLabs}
                  disabled={savingElevenLabs || !elevenLabsVoiceId || !elevenLabsApiKey}
                  className="flex-1 btn-primary py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {savingElevenLabs ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Terms & Conditions Modal */}
      <AnimatePresence>
        {showTermsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowTermsModal(false)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl max-h-[85vh] glass-heavy rounded-2xl shadow-2xl flex flex-col"
              data-testid="terms-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Terms & Conditions</h2>
                    <p className="text-sm text-white/50">Effective: February 2, 2026</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="prose prose-invert prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-white/80 leading-relaxed">
                    {termsContent}
                  </pre>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-white/10">
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="w-full btn-primary py-3 rounded-full flex items-center justify-center gap-2"
                  data-testid="close-terms-btn"
                >
                  <Check className="w-5 h-5" />
                  I Understand
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== PLAYER PAGE ====================
const PlayerPage = ({ user }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const pollingRef = useRef(null);

  const steps = ['Extracting', 'Transcribing', 'Translating', 'Generating'];

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        console.log(`[AUDIOGOLA] Fetching status for job: ${jobId}`);
        const res = await axios.get(`${API}/translate/status/${jobId}`);
        console.log(`[AUDIOGOLA] Job status:`, { status: res.data.status, progress: res.data.progress, error: res.data.error_message });
        setJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(pollingRef.current);
        }
      } catch (error) {
        console.error("[AUDIOGOLA] Failed to fetch job status:", error);
        // If job not found, show error
        if (error.response?.status === 404) {
          setJob({ status: 'failed', error_message: 'Translation job not found. It may have expired or the link is invalid.' });
          clearInterval(pollingRef.current);
        }
      }
    };
    fetchStatus();
    pollingRef.current = setInterval(fetchStatus, 2000);
    return () => clearInterval(pollingRef.current);
  }, [jobId]);

  const handleSave = async () => {
    if (!user) {
      // Store the current page URL so we can return after auth
      const redirectUrl = window.location.href;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    try {
      await axios.post(`${API}/library/save?job_id=${jobId}`, {}, { withCredentials: true });
      setIsSaved(true);
      toast.success("Saved to library!");
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) audioRef.current.pause();
      else audioRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };
  
  const handleDownload = async () => {
    if (!job.audio_chunks || job.audio_chunks.length === 0) {
      toast.error("No audio to download");
      return;
    }
    
    try {
      // Combine all audio chunks into one file
      const audioBlobs = await Promise.all(
        job.audio_chunks.map(async (chunk) => {
          const response = await fetch(chunk);
          return response.blob();
        })
      );
      
      // Create combined blob
      const combinedBlob = new Blob(audioBlobs, { type: 'audio/mpeg' });
      
      // Create download link
      const url = URL.createObjectURL(combinedBlob);
      const a = document.createElement('a');
      a.href = url;
      const filename = (job.metadata?.title || 'translated_audio')
        .replace(/[^a-z0-9]/gi, '_')
        .substring(0, 50);
      a.download = `${filename}_${job.target_language}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Download started!");
    } catch (error) {
      toast.error("Failed to download audio");
    }
  };

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-amber-200/60" />
      </div>
    );
  }

  if (job.status === 'failed') {
    // Parse and display user-friendly error message
    let displayError = job.error_message || "An unexpected error occurred";
    let suggestion = "Please try uploading again or try with a different file.";
    
    if (displayError.includes("ffprobe") || displayError.includes("ffmpeg")) {
      displayError = "Audio processing service temporarily unavailable";
      suggestion = "Please wait a moment and try again. The service is restarting.";
    } else if (displayError.includes("transcribe")) {
      displayError = "Failed to extract speech from audio";
      suggestion = "Make sure your file contains clear spoken audio.";
    } else if (displayError.includes("APIConnectionError") || displayError.includes("network")) {
      displayError = "Connection error during processing";
      suggestion = "Please check your internet connection and try again.";
    } else if (displayError.includes("not found") || displayError.includes("expired")) {
      displayError = "Translation job not found";
      suggestion = "This job may have expired. Please start a new translation.";
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center px-6 pt-32">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-heavy rounded-3xl p-10 text-center max-w-lg"
        >
          <AlertCircle className="w-14 h-14 text-red-400/80 mx-auto mb-6" />
          <h2 className="text-2xl font-semibold mb-3">Translation Failed</h2>
          <p className="text-white/70 mb-4">{displayError}</p>
          <p className="text-white/40 text-sm mb-8">{suggestion}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate("/")} className="btn-primary px-8 py-3 rounded-full">
              Try Again
            </button>
            <button 
              onClick={() => window.location.reload()} 
              className="px-8 py-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <RefreshCw className="w-5 h-5 inline mr-2" />
              Refresh
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (job.status !== 'completed') {
    const stepIndex = ['extracting', 'transcribing', 'translating', 'generating_audio'].indexOf(job.status);
    return (
      <div className="min-h-screen flex items-center justify-center px-6 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-3xl p-10 text-center max-w-xl w-full"
        >
          <div className="w-16 h-16 mx-auto mb-8 glass-card rounded-2xl flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-amber-200/80" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">Processing Your Audio</h2>
          <p className="text-white/50 mb-8">{job.metadata?.title || "Translating..."}</p>
          
          <div className="flex justify-center gap-2 mb-8 flex-wrap">
            {steps.map((step, i) => (
              <div key={step} className={`px-3 py-1 rounded-full text-sm ${
                i < stepIndex ? 'bg-emerald-500/20 text-emerald-300' :
                i === stepIndex ? 'bg-amber-200/20 text-amber-200' :
                'bg-white/5 text-white/30'
              }`}>
                {i <= stepIndex && <span className="mr-1">{i < stepIndex ? '✓' : '●'}</span>}
                {step}
              </div>
            ))}
          </div>
          
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${job.progress}%` }}
              className="h-full progress-bar rounded-full"
            />
          </div>
          <p className="text-sm text-white/40 mt-3">{job.progress}%</p>
        </motion.div>
      </div>
    );
  }

  // Completed - show player
  return (
    <div className="min-h-screen px-6 pt-32 pb-20">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-3xl p-8"
          data-testid="audio-player"
        >
          <div className="flex items-center gap-6 mb-8">
            <div className="w-20 h-20 glass-card rounded-2xl flex items-center justify-center">
              <Headphones className="w-10 h-10" style={{ color: '#3DD0D6' }} />
            </div>
            <div>
              <h2 className="text-2xl font-semibold mb-1 text-white">{job.metadata?.title || 'Translated Audio'}</h2>
              <p className="text-white/60">{job.target_language}</p>
            </div>
          </div>

          <audio
            ref={audioRef}
            src={job.audio_chunks?.[currentChunk]}
            onTimeUpdate={() => {
              if (audioRef.current) {
                setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
              }
            }}
            onEnded={() => {
              if (currentChunk < job.audio_chunks.length - 1) {
                setCurrentChunk(currentChunk + 1);
              } else {
                setIsPlaying(false);
              }
            }}
          />

          <div 
            className="h-2 bg-white/10 rounded-full mb-6 cursor-pointer"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const percent = (e.clientX - rect.left) / rect.width;
              if (audioRef.current) {
                audioRef.current.currentTime = percent * audioRef.current.duration;
              }
            }}
          >
            <div className="h-full progress-bar rounded-full" style={{ width: `${progress}%` }} />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => currentChunk > 0 && setCurrentChunk(currentChunk - 1)}
                className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              >
                <SkipBack className="w-5 h-5" />
              </button>
              <button
                onClick={togglePlay}
                className="p-4 rounded-full btn-primary"
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
              </button>
              <button
                onClick={() => currentChunk < job.audio_chunks.length - 1 && setCurrentChunk(currentChunk + 1)}
                className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              >
                <SkipForward className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full btn-secondary text-sm sm:text-base"
                data-testid="download-btn"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={handleSave}
                disabled={isSaved}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full transition-all text-sm sm:text-base ${
                  isSaved ? 'bg-emerald-500/20 text-emerald-300' : 'btn-secondary'
                }`}
                data-testid="save-btn"
              >
                {isSaved ? <Check className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                {isSaved ? 'Saved' : 'Save'}
              </button>
            </div>
          </div>
          
          {/* New Translation Button */}
          <div className="mt-6 pt-6 border-t border-white/10 flex justify-center">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 px-6 py-3 rounded-full btn-secondary"
              data-testid="new-translation-btn"
            >
              <Upload className="w-4 h-4" />
              New Translation
            </button>
          </div>
        </motion.div>

        {job.translated_text && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 glass-card rounded-2xl p-6"
          >
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Languages className="w-5 h-5 text-cyan-300/80" />
              Transcript
            </h3>
            <p className="text-white/60 leading-relaxed text-sm whitespace-pre-wrap">
              {job.translated_text}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ==================== VIDEO PLAYER PAGE ====================
const VideoPlayerPage = ({ user }) => {
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const pollingRef = useRef(null);
  
  const sessionId = location.state?.sessionId;

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/video/status/${jobId}`);
        setJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(pollingRef.current);
        }
      } catch (error) {
        console.error("Failed to fetch video job status:", error);
        if (error.response?.status === 404) {
          setJob({ status: 'failed', error_message: 'Video translation job not found or expired.' });
          clearInterval(pollingRef.current);
        }
      }
    };
    fetchStatus();
    pollingRef.current = setInterval(fetchStatus, 3000);
    return () => clearInterval(pollingRef.current);
  }, [jobId]);

  // Warn user before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (job?.status === 'completed' && !isDownloading) {
        e.preventDefault();
        e.returnValue = 'Your translated video will be deleted if you leave without downloading. Are you sure?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [job?.status, isDownloading]);

  // Cleanup session when navigating away
  const handleNavigateAway = async (destination) => {
    if (job?.status === 'completed' && sessionId) {
      setShowDownloadWarning(true);
      return;
    }
    navigate(destination);
  };

  const confirmNavigateAway = async () => {
    if (sessionId) {
      try {
        await axios.post(`${API}/video/cleanup/${sessionId}`);
      } catch (e) {
        console.error('Cleanup failed:', e);
      }
    }
    navigate('/');
  };

  const handleDownload = async () => {
    if (!job?.download_url || !sessionId) {
      toast.error("Download not available");
      return;
    }
    
    setIsDownloading(true);
    try {
      // Use fetch for better streaming support on mobile
      const response = await fetch(`${API}/video/download/${sessionId}`, {
        method: 'GET',
        headers: {
          'Accept': 'video/mp4',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error("Downloaded file is empty");
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'translated_video.mp4');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Small delay before revoking to ensure download starts
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
      toast.success("Download complete!");
      
      // Cleanup session after successful download
      try {
        await axios.post(`${API}/video/cleanup/${sessionId}`);
      } catch (cleanupError) {
        console.log("Session cleanup handled by backend");
      }
      
      setTimeout(() => navigate('/'), 2000);
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || "Download failed. Please try again.");
      setIsDownloading(false);
    }
  };

  const getProgressSteps = () => {
    const steps = [
      { key: 'pending', label: 'Preparing' },
      { key: 'chunking', label: 'Splitting Video' },
      { key: 'processing', label: 'Translating' },
      { key: 'merging', label: 'Assembling' },
      { key: 'completed', label: 'Ready' }
    ];
    
    const currentIndex = steps.findIndex(s => s.key === job?.status);
    return steps.map((step, i) => ({
      ...step,
      completed: i < currentIndex,
      active: i === currentIndex
    }));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a1628' }}>
      <Navbar user={user} />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back button */}
        <button
          onClick={() => handleNavigateAway('/')}
          className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Home
        </button>

        {job?.status === 'failed' ? (
          // Error state
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-heavy rounded-3xl p-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Translation Failed</h2>
            <p className="text-white/60 mb-6">{job.error_message || 'An error occurred during video translation.'}</p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary px-6 py-3 rounded-xl"
            >
              Try Again
            </button>
          </motion.div>
        ) : job?.status === 'completed' ? (
          // Completed state
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-heavy rounded-3xl p-8"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">Video Ready!</h2>
              <p className="text-white/60">Your translated video is ready for download.</p>
            </div>
            
            {/* Preview */}
            {job.preview_url && sessionId && (
              <div className="mb-8">
                <h3 className="text-sm text-white/60 mb-3">Preview (30 seconds)</h3>
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full rounded-xl bg-black"
                  style={{ maxHeight: '400px' }}
                  onError={(e) => {
                    console.error('Video preview error:', e);
                  }}
                >
                  <source src={`${API}/video/preview/${sessionId}`} type="video/mp4" />
                  Your browser does not support video playback.
                </video>
              </div>
            )}
            
            {/* Warning */}
            <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>This video is stored temporarily and will be <strong>permanently deleted</strong> after download or if you navigate away.</span>
              </p>
            </div>
            
            {/* Download button */}
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="w-full btn-primary py-4 rounded-xl flex items-center justify-center gap-3 text-lg"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Download Translated Video
                </>
              )}
            </button>
          </motion.div>
        ) : (
          // Processing state
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-heavy rounded-3xl p-8"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-400/20 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">Translating Your Video</h2>
              <p className="text-white/60">This may take several minutes. Please don&apos;t close this tab.</p>
            </div>
            
            {/* Progress steps */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                {getProgressSteps().map((step, i) => (
                  <div key={step.key} className="flex flex-col items-center flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                      step.completed ? 'bg-green-500' : step.active ? 'bg-cyan-400' : 'bg-white/10'
                    }`}>
                      {step.completed ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : step.active ? (
                        <Loader2 className="w-4 h-4 text-black animate-spin" />
                      ) : (
                        <span className="text-xs text-white/40">{i + 1}</span>
                      )}
                    </div>
                    <span className={`text-xs ${step.active ? 'text-cyan-400' : 'text-white/40'}`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-white/60">Progress</span>
                <span className="text-cyan-400">{job?.progress || 0}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-400 to-green-400"
                  initial={{ width: 0 }}
                  animate={{ width: `${job?.progress || 0}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
            
            {/* Chunk progress */}
            {job?.total_chunks > 0 && (
              <p className="text-center text-sm text-white/40">
                Processing chunk {job.current_chunk || 1} of {job.total_chunks}
              </p>
            )}
          </motion.div>
        )}
      </div>

      {/* Navigation warning modal */}
      <AnimatePresence>
        {showDownloadWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-heavy rounded-3xl p-8 max-w-md w-full text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Leave Without Downloading?</h3>
              <p className="text-white/60 mb-6">
                Your translated video will be <strong>permanently deleted</strong> if you leave without downloading.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDownloadWarning(false)}
                  className="flex-1 py-3 rounded-xl border border-white/10 hover:bg-white/5 transition-colors"
                >
                  Stay & Download
                </button>
                <button
                  onClick={confirmNavigateAway}
                  className="flex-1 py-3 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Leave Anyway
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== LIBRARY PAGE ====================
const LibraryPage = ({ user }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    axios.get(`${API}/library`, { withCredentials: true })
      .then(res => setItems(res.data))
      .finally(() => setIsLoading(false));
  }, [user, navigate]);

  const handleDelete = async (itemId) => {
    await axios.delete(`${API}/library/${itemId}`, { withCredentials: true });
    setItems(items.filter(i => i.item_id !== itemId));
    toast.success("Removed");
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-amber-200/60" /></div>;

  return (
    <div className="min-h-screen px-6 pt-32 pb-20" data-testid="library-page">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#F8FAFC' }}>Your <span style={{ color: '#3DD0D6' }}>Library</span></h1>
          <p className="text-white/50">Saved translations, ready anytime.</p>
        </motion.div>

        {items.length === 0 ? (
          <div className="glass-card rounded-3xl p-12 text-center">
            <Library className="w-14 h-14 text-white/20 mx-auto mb-6" />
            <h2 className="text-xl font-semibold mb-3">Empty library</h2>
            <p className="text-white/50 mb-6">Start translating to build your collection.</p>
            <button onClick={() => navigate("/")} className="btn-primary px-8 py-3 rounded-full">
              Upload Audio
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item, i) => (
              <motion.div
                key={item.item_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-5 morph-card group"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-200/10 flex items-center justify-center">
                    <Headphones className="w-6 h-6 text-amber-200/80" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{item.title}</h3>
                    <p className="text-sm text-white/40">{item.target_language}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(item.item_id)}
                    className="p-2 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                <button
                  onClick={() => navigate(`/player/${item.job_id}`)}
                  className="w-full py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4" /> Listen
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== PRICING PAGE ====================
const PricingPage = ({ user }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [topups, setTopups] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [selectedTopup, setSelectedTopup] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [currency, setCurrency] = useState('USD'); // USD or INR
  
  // Waitlist modal state
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [selectedPlanForWaitlist, setSelectedPlanForWaitlist] = useState(null);
  const [waitlistForm, setWaitlistForm] = useState({ fullName: '', email: '', country: '' });
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [submittingWaitlist, setSubmittingWaitlist] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch pricing plans (public endpoint - no credentials needed)
      try {
        const pricingRes = await axios.get(`${API}/pricing/plans`);
        setPlans(pricingRes.data.plans);
        setTopups(pricingRes.data.topups);
      } catch (error) {
        console.error("Failed to fetch pricing plans:", error);
      }
      
      // Fetch subscription separately (requires credentials)
      try {
        const subRes = await axios.get(`${API}/subscription`, { withCredentials: true });
        setSubscription(subRes.data);
      } catch (error) {
        // User may not be logged in - this is expected
        console.log("Subscription fetch skipped (user not logged in)");
      }
      
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const handleUpgrade = async (planId) => {
    // Free tier - direct access
    if (planId === 'free') {
      navigate('/');
      return;
    }
    
    // Paid tiers - show waitlist modal
    const plan = plans.find(p => p.id === planId);
    setSelectedPlanForWaitlist(plan);
    setShowWaitlistModal(true);
    setWaitlistSubmitted(false);
    setWaitlistForm({ fullName: '', email: '', country: '' });
  };

  const handleWaitlistSubmit = async () => {
    if (!waitlistForm.fullName || !waitlistForm.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    
    setSubmittingWaitlist(true);
    try {
      await axios.post(`${API}/waitlist/register`, {
        plan_id: selectedPlanForWaitlist?.id,
        full_name: waitlistForm.fullName,
        email: waitlistForm.email,
        country: waitlistForm.country || null
      });
      setWaitlistSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register. Please try again.");
    } finally {
      setSubmittingWaitlist(false);
    }
  };

  const handlePurchaseTopup = async () => {
    if (!selectedTopup) return;
    
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/topup/purchase`,
        { topup_id: selectedTopup.id },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      setShowTopupModal(false);
      // Refresh subscription
      const subRes = await axios.get(`${API}/subscription`, { withCredentials: true });
      setSubscription(subRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to purchase");
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (plan) => {
    if (currency === 'INR') {
      return plan.priceINR === 0 ? 'Free' : `₹${plan.priceINR}`;
    }
    return plan.priceUSD === 0 ? 'Free' : `$${plan.priceUSD}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 pt-32 pb-20" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
            Simple, <span style={{ color: '#3DD0D6' }}>Transparent</span> Pricing
          </h1>
          <p className="text-lg text-white/50 mb-6">
            All pricing shown in audio minutes. Pay for what you use.
          </p>
          
          {/* Currency Toggle */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <button
              onClick={() => setCurrency('USD')}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                currency === 'USD' ? 'bg-cyan-500/20 text-cyan-300' : 'text-white/50 hover:text-white/70'
              }`}
            >
              USD ($)
            </button>
            <button
              onClick={() => setCurrency('INR')}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                currency === 'INR' ? 'bg-cyan-500/20 text-cyan-300' : 'text-white/50 hover:text-white/70'
              }`}
            >
              INR (₹)
            </button>
          </div>

          {/* Current Usage Bar (if authenticated) */}
          {subscription?.authenticated && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-md mx-auto glass-card rounded-2xl p-4 mb-8"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/60">Current Plan: <span className="text-white font-medium">{subscription.plan_title}</span></span>
                <span className="text-sm text-cyan-400">{subscription.total_available} min left</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all"
                  style={{ width: `${Math.min(subscription.usage_percent, 100)}%` }}
                />
              </div>
              {subscription.should_show_upgrade && (
                <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Running low on minutes - consider upgrading!
                </p>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-16">
          {isLoading ? (
            // Skeleton Loading Animation
            [...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.05 }}
                className="glass-card rounded-2xl p-6 relative flex flex-col"
                data-testid={`plan-skeleton-${i}`}
              >
                {/* Skeleton Title */}
                <div className="mb-4">
                  <div className="h-6 w-24 skeleton-shimmer rounded-lg mb-2"></div>
                  <div className="h-3 w-32 skeleton-shimmer rounded"></div>
                </div>
                
                {/* Skeleton Price */}
                <div className="mb-4">
                  <div className="h-9 w-20 skeleton-shimmer rounded-lg"></div>
                </div>
                
                {/* Skeleton Features */}
                <div className="space-y-3 flex-1">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-cyan-500/20 rounded"></div>
                      <div className={`h-4 skeleton-shimmer rounded`} style={{ width: `${60 + j * 8}%` }}></div>
                    </div>
                  ))}
                </div>
                
                {/* Skeleton Button */}
                <div className="mt-6 h-12 skeleton-shimmer rounded-full"></div>
              </motion.div>
            ))
          ) : (
            plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`glass-card rounded-2xl p-6 relative flex flex-col ${
                plan.highlight ? 'ring-2 ring-cyan-400 shadow-lg shadow-cyan-500/20' : ''
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-cyan-500 text-white text-xs font-medium rounded-full">
                  POPULAR
                </div>
              )}
              
              
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-white mb-1">{plan.title}</h3>
                <p className="text-xs text-white/40">{plan.tagline}</p>
              </div>
              
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">{formatPrice(plan)}</span>
                {plan.priceUSD > 0 && <span className="text-white/40">/mo</span>}
              </div>
              
              <div className="space-y-3 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <Timer className="w-4 h-4 text-cyan-400" />
                  <span className="text-white/70">
                    {plan.id === 'unlimited' ? `Up to ${plan.minutes} min (fair use)` : `${plan.minutes} min/month`}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Gauge className="w-4 h-4 text-cyan-400" />
                  <span className="text-white/70">{plan.maxMinutesPerAudio} min max per audio</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  <span className="text-white/70">{plan.languages}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Volume2 className="w-4 h-4 text-cyan-400" />
                  <span className="text-white/70">{plan.voices}</span>
                </div>
                {plan.voiceCloning && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mic className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-300">Voice cloning (1 voice)</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span className="text-white/70">{plan.processing} processing</span>
                </div>
              </div>
              
              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={subscription?.plan_id === plan.id}
                className={`mt-6 w-full py-3 rounded-full text-sm font-medium transition-all ${
                  subscription?.plan_id === plan.id
                    ? 'bg-white/10 text-white/50 cursor-not-allowed'
                    : plan.highlight
                      ? 'btn-primary'
                      : 'btn-secondary'
                }`}
                data-testid={`cta-${plan.id}`}
              >
                {subscription?.plan_id === plan.id 
                  ? 'Current Plan' 
                  : plan.id === 'free' 
                    ? 'Get Started' 
                    : 'Join Waitlist'}
              </button>
            </motion.div>
          ))
          )}
        </div>

        {/* Top-ups Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="text-2xl font-semibold text-center mb-2 text-white">
            Need More Minutes?
          </h2>
          <p className="text-center text-white/50 mb-8">
            Buy additional minutes that don&apos;t expire for 6 months.
          </p>
          
          <div className="grid sm:grid-cols-3 gap-4">
            {isLoading ? (
              // Skeleton Loading for Topups
              [...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                  className="glass-card rounded-xl p-5"
                >
                  <div className="h-7 w-20 skeleton-shimmer rounded-lg mb-2"></div>
                  <div className="h-5 w-12 skeleton-shimmer rounded mb-2"></div>
                  <div className="h-3 w-28 skeleton-shimmer rounded"></div>
                </motion.div>
              ))
            ) : (
              topups.map((topup, i) => (
              <motion.div
                key={topup.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1 }}
                className={`glass-card rounded-xl p-5 cursor-pointer transition-all hover:ring-2 hover:ring-cyan-400/50 ${
                  topup.highlight ? 'ring-2 ring-amber-400' : ''
                }`}
                onClick={() => {
                  if (!user) {
                    const redirectUrl = window.location.origin + '/pricing';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    return;
                  }
                  setSelectedTopup(topup);
                  setShowTopupModal(true);
                }}
                data-testid={`topup-${topup.id}`}
              >
                {topup.highlight && (
                  <span className="text-xs text-amber-400 font-medium mb-2 block">BEST VALUE</span>
                )}
                <div className="text-2xl font-bold text-white mb-1">{topup.minutes} min</div>
                <div className="text-lg text-cyan-400 mb-2">
                  {currency === 'INR' ? `₹${topup.priceINR}` : `$${topup.priceUSD}`}
                </div>
                <div className="text-xs text-white/40">Valid for {topup.expiryMonths} months</div>
              </motion.div>
            ))
            )}
          </div>
        </motion.div>

        {/* Voice Cloning Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 rounded-full">
            <Mic className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300">
              Voice cloning: <strong>1 voice per user</strong> (Pro plans and above)
            </span>
          </div>
        </motion.div>
      </div>

      {/* Top-up Purchase Modal */}
      <AnimatePresence>
        {showTopupModal && selectedTopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowTopupModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-heavy rounded-2xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="topup-modal"
            >
              <h3 className="text-xl font-semibold mb-4 text-white">Buy Minutes</h3>
              
              <div className="glass-card rounded-xl p-4 mb-6">
                <div className="text-3xl font-bold text-white mb-1">{selectedTopup.minutes} minutes</div>
                <div className="text-xl text-cyan-400">
                  {currency === 'INR' ? `₹${selectedTopup.priceINR}` : `$${selectedTopup.priceUSD}`}
                </div>
                <div className="text-sm text-white/40 mt-2">
                  Valid for {selectedTopup.expiryMonths} months from purchase
                </div>
              </div>

              <div className="bg-amber-500/10 rounded-lg p-3 mb-6">
                <p className="text-xs text-amber-300 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  This is a demo. Payment integration coming soon.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowTopupModal(false)}
                  className="flex-1 btn-secondary py-3 rounded-full"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurchaseTopup}
                  disabled={processing}
                  className="flex-1 btn-primary py-3 rounded-full flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Buy Now
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Waitlist Modal for Paid Plans */}
      <AnimatePresence>
        {showWaitlistModal && selectedPlanForWaitlist && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowWaitlistModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-heavy rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="waitlist-modal"
            >
              {!waitlistSubmitted ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-cyan-400" />
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-2">Thanks for your interest!</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      We&apos;re currently setting up subscriptions for global users. Register below and we&apos;ll notify you as soon as paid plans are live. Until then, enjoy the Free tier!
                    </p>
                  </div>

                  <div className="glass-card rounded-xl p-3 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <div className="font-medium text-white">{selectedPlanForWaitlist.title} Plan</div>
                      <div className="text-sm text-white/50">
                        {selectedPlanForWaitlist.minutes} min/month • {formatPrice(selectedPlanForWaitlist)}/mo
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={waitlistForm.fullName}
                      onChange={(e) => setWaitlistForm({ ...waitlistForm, fullName: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none"
                      data-testid="waitlist-name"
                    />
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={waitlistForm.email}
                      onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none"
                      data-testid="waitlist-email"
                    />
                    <input
                      type="text"
                      placeholder="Country (optional)"
                      value={waitlistForm.country}
                      onChange={(e) => setWaitlistForm({ ...waitlistForm, country: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-cyan-400 focus:outline-none"
                      data-testid="waitlist-country"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowWaitlistModal(false)}
                      className="flex-1 btn-secondary py-3 rounded-full"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleWaitlistSubmit}
                      disabled={submittingWaitlist || !waitlistForm.fullName || !waitlistForm.email}
                      className="flex-1 btn-primary py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="waitlist-submit"
                    >
                      {submittingWaitlist ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Check className="w-5 h-5" />
                          Notify Me
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center"
                  >
                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  </motion.div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Thank you!</h3>
                  <p className="text-white/60 mb-6">
                    We&apos;ll notify you when subscriptions go live.
                  </p>
                  <button
                    onClick={() => {
                      setShowWaitlistModal(false);
                      navigate('/');
                    }}
                    className="btn-primary px-8 py-3 rounded-full"
                    data-testid="waitlist-continue"
                  >
                    Continue with Free Tier
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== AUTH PAGES ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    if (!sessionIdMatch) { navigate("/"); return; }
    axios.post(`${API}/auth/session`, { session_id: sessionIdMatch[1] }, { withCredentials: true })
      .then((res) => {
        // Check if user has accepted terms
        if (!res.data.terms_accepted) {
          navigate("/accept-terms");
        } else {
          navigate("/");
        }
      })
      .catch(() => navigate("/"));
  }, [location, navigate]);

  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-amber-200/60" /></div>;
};

// ==================== TERMS ACCEPTANCE PAGE ====================
const TermsAcceptancePage = ({ user, onAcceptTerms }) => {
  const navigate = useNavigate();
  const [termsContent, setTermsContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    // If no user, redirect to login
    if (!user) {
      navigate("/");
      return;
    }
    // If user already accepted terms, redirect to home
    if (user.terms_accepted) {
      navigate("/");
      return;
    }
    
    // Fetch terms content
    axios.get(`${API}/terms`)
      .then(res => setTermsContent(res.data.content))
      .catch(() => toast.error("Failed to load terms"));
  }, [user, navigate]);

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcceptTerms = async () => {
    if (!agreedToTerms) {
      toast.error("Please check the box to accept the Terms & Conditions");
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/accept-terms`, {}, { withCredentials: true });
      toast.success("Terms accepted! Welcome to AUDIOGOLA");
      
      // Update user state in parent
      if (onAcceptTerms) {
        onAcceptTerms();
      }
      
      navigate("/");
    } catch (error) {
      toast.error("Failed to accept terms. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-amber-200/60" /></div>;
  }

  return (
    <div className="min-h-screen noise hero-gradient flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl glass-heavy rounded-3xl shadow-2xl"
        data-testid="terms-acceptance-page"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 text-center">
          <div className="flex justify-center mb-4">
            <img src="/audiogola-logo.svg" alt="AUDIOGOLA" className="h-16" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Welcome to AUDIOGOLA</h1>
          <p className="text-white/60">Please review and accept our Terms & Conditions to continue</p>
          {user && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-white/50">
              <span>Signing up as</span>
              <span className="text-cyan-400">{user.email}</span>
            </div>
          )}
        </div>

        {/* Terms Content */}
        <div 
          ref={contentRef}
          onScroll={handleScroll}
          className="h-[400px] overflow-y-auto p-6"
        >
          <pre className="whitespace-pre-wrap font-sans text-sm text-white/80 leading-relaxed">
            {termsContent || "Loading terms..."}
          </pre>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 space-y-4">
          {/* Scroll hint */}
          {!hasScrolledToBottom && (
            <div className="flex items-center justify-center gap-2 text-amber-400 text-sm">
              <ChevronDown className="w-4 h-4 animate-bounce" />
              <span>Please scroll down to read the full terms</span>
              <ChevronDown className="w-4 h-4 animate-bounce" />
            </div>
          )}

          {/* Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="w-5 h-5 rounded mt-0.5 accent-cyan-500"
              data-testid="accept-terms-checkbox"
            />
            <span className="text-sm text-white/80">
              I have read and agree to the Terms & Conditions and Privacy Policy
            </span>
          </label>

          {/* Accept Button */}
          <button
            onClick={handleAcceptTerms}
            disabled={isLoading || !agreedToTerms}
            className="w-full btn-primary py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-50 text-lg"
            data-testid="accept-terms-btn"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Accept & Continue
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  useEffect(() => { 
    // Redirect to home instead of causing infinite reload
    navigate("/"); 
  }, [navigate]);
  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-amber-200/60" /></div>;
};

// ==================== ADMIN PANEL COMPONENTS ====================

// Admin Layout wrapper
const AdminPageLayout = ({ children, user, onLogout, activeTab }) => {
  const navigate = useNavigate();
  
  const menuItems = [
    { id: "dashboard", label: "Dashboard", path: "/admin" },
    { id: "users", label: "Users", path: "/admin/users" },
    { id: "translations", label: "Translations", path: "/admin/translations" },
    { id: "waitlist", label: "Waitlist", path: "/admin/waitlist" },
    { id: "system", label: "System", path: "/admin/system" },
  ];

  return (
    <div className="min-h-screen bg-[hsl(220,30%,6%)]">
      <header className="bg-[hsl(220,25%,8%)] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/audiogola-logo.svg" alt="AUDIOGOLA" className="h-8" />
            <span className="text-lg font-semibold text-white">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-white/60 hover:text-white text-sm flex items-center gap-2">
              <Home className="w-4 h-4" /> Back to Site
            </button>
            <div className="flex items-center gap-2">
              <span className="text-white/70 text-sm">{user?.name}</span>
              <button onClick={onLogout} className="p-2 rounded-lg hover:bg-white/5 text-white/60">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-6 flex gap-1 pb-2">
          {menuItems.map(item => (
            <button 
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === item.id 
                  ? 'bg-cyan-500/20 text-cyan-400' 
                  : 'text-white/60 hover:bg-white/5'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
};

// Metric Card component
const AdminMetricCard = ({ title, value, icon: Icon, color = "cyan" }) => {
  const colors = {
    cyan: "bg-cyan-500/20 text-cyan-400",
    green: "bg-emerald-500/20 text-emerald-400",
    amber: "bg-amber-500/20 text-amber-400",
    purple: "bg-purple-500/20 text-purple-400",
  };
  return (
    <div className="bg-[hsl(220,25%,10%)] rounded-2xl p-6 border border-white/5">
      <div className={`w-12 h-12 rounded-xl ${colors[color]} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-white/50">{title}</p>
    </div>
  );
};

// Admin Dashboard Page
const AdminDashboardPage = ({ user, onLogout }) => {
  const [metrics, setMetrics] = useState(null);
  const [charts, setCharts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/dashboard`, { withCredentials: true }),
      axios.get(`${API}/admin/dashboard/charts?days=14`, { withCredentials: true })
    ]).then(([metricsRes, chartsRes]) => {
      setMetrics(metricsRes.data);
      setCharts(chartsRes.data);
    }).catch(() => toast.error("Failed to load dashboard")).finally(() => setIsLoading(false));
  }, []);

  return (
    <AdminPageLayout user={user} onLogout={onLogout} activeTab="dashboard">
      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
      ) : (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <AdminMetricCard title="Total Users" value={metrics?.total_users || 0} icon={Users} color="cyan" />
            <AdminMetricCard title="Daily Active" value={metrics?.dau || 0} icon={Activity} color="green" />
            <AdminMetricCard title="Translations Today" value={metrics?.translations_today || 0} icon={Languages} color="purple" />
            <AdminMetricCard title="Success Rate" value={`${metrics?.success_rate || 100}%`} icon={CheckCircle2} color="green" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <AdminMetricCard title="Monthly Active" value={metrics?.mau || 0} icon={Users} color="amber" />
            <AdminMetricCard title="New Users (7d)" value={metrics?.new_users_week || 0} icon={TrendingUp} color="cyan" />
            <AdminMetricCard title="Waitlist" value={metrics?.waitlist_total || 0} icon={ClipboardList} color="purple" />
            <AdminMetricCard title="Active Jobs" value={metrics?.active_jobs || 0} icon={Zap} color="amber" />
          </div>
          {charts?.daily_translations && (
            <div className="bg-[hsl(220,25%,10%)] rounded-2xl p-6 border border-white/5">
              <h3 className="text-lg font-semibold text-white mb-4">Daily Translations (14 days)</h3>
              <div className="h-32 flex items-end gap-1">
                {charts.daily_translations.map((day, i) => {
                  const max = Math.max(...charts.daily_translations.map(d => d.count), 1);
                  return (
                    <div key={i} className="flex-1 bg-cyan-500 rounded-t" style={{ height: `${(day.count / max) * 100}%`, minHeight: '2px' }} title={`${day.date}: ${day.count}`} />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </AdminPageLayout>
  );
};

// Admin Users Page
const AdminUsersPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/users`, { params: { page: 1, page_size: 50, search }, withCredentials: true })
      .then(res => { setUsers(res.data.users); setTotal(res.data.total); })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setIsLoading(false));
  }, [search]);

  return (
    <AdminPageLayout user={user} onLogout={onLogout} activeTab="users">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Users ({total})</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30" />
          </div>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : (
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl border border-white/5 overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/50 font-medium">User</th>
                <th className="text-left p-4 text-white/50 font-medium">Plan</th>
                <th className="text-left p-4 text-white/50 font-medium">Translations</th>
                <th className="text-left p-4 text-white/50 font-medium">Minutes</th>
                <th className="text-left p-4 text-white/50 font-medium">Actions</th>
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center overflow-hidden">
                          {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <Users className="w-5 h-5 text-cyan-400" />}
                        </div>
                        <div><p className="text-white font-medium">{u.name}</p><p className="text-white/50 text-sm">{u.email}</p></div>
                      </div>
                    </td>
                    <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-cyan-500/20 text-cyan-400">{u.plan_id}</span></td>
                    <td className="p-4 text-white">{u.translation_count}</td>
                    <td className="p-4 text-white">{u.minutes_remaining}</td>
                    <td className="p-4"><button onClick={() => navigate(`/admin/users/${u.user_id}`)} className="p-2 rounded-lg hover:bg-white/10 text-white/60"><Eye className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminPageLayout>
  );
};

// Admin User Details Page
const AdminUserDetailsPage = ({ user, onLogout }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [bonusMinutes, setBonusMinutes] = useState(10);

  useEffect(() => {
    axios.get(`${API}/admin/users/${userId}`, { withCredentials: true })
      .then(res => setUserData(res.data))
      .catch(() => toast.error("Failed to load user"))
      .finally(() => setIsLoading(false));
  }, [userId]);

  const handleAddMinutes = async () => {
    try {
      await axios.post(`${API}/admin/users/${userId}/add-minutes`, { minutes: bonusMinutes }, { withCredentials: true });
      toast.success(`Added ${bonusMinutes} minutes`);
      const res = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUserData(res.data);
    } catch { toast.error("Failed to add minutes"); }
  };

  return (
    <AdminPageLayout user={user} onLogout={onLogout} activeTab="users">
      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
      ) : userData ? (
        <div className="space-y-6">
          <button onClick={() => navigate("/admin/users")} className="text-white/60 hover:text-white">← Back to Users</button>
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl p-6 border border-white/5 flex items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-cyan-500/20 flex items-center justify-center overflow-hidden">
              {userData.user.picture ? <img src={userData.user.picture} alt="" className="w-full h-full object-cover" /> : <Users className="w-10 h-10 text-cyan-400" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{userData.user.name}</h1>
              <p className="text-white/50">{userData.user.email}</p>
              <span className="inline-block mt-2 px-3 py-1 rounded-full text-sm bg-cyan-500/20 text-cyan-400">{userData.subscription.plan_id} Plan</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <AdminMetricCard title="Translations" value={userData.stats.total_translations} icon={Languages} color="cyan" />
            <AdminMetricCard title="Success Rate" value={`${userData.stats.success_rate}%`} icon={CheckCircle2} color="green" />
            <AdminMetricCard title="Minutes Left" value={userData.subscription.minutes_remaining} icon={Clock} color="amber" />
            <AdminMetricCard title="Library Items" value={userData.stats.library_items} icon={FileAudio} color="purple" />
          </div>
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl p-6 border border-white/5">
            <h3 className="text-lg font-semibold text-white mb-4">Add Bonus Minutes</h3>
            <div className="flex items-center gap-4">
              <input type="number" value={bonusMinutes} onChange={(e) => setBonusMinutes(parseInt(e.target.value) || 0)} className="w-24 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white" />
              <button onClick={handleAddMinutes} className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-medium">Add Minutes</button>
            </div>
          </div>
        </div>
      ) : <p className="text-white/50">User not found</p>}
    </AdminPageLayout>
  );
};

// Admin Translations Page
const AdminTranslationsPage = ({ user, onLogout }) => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/translations`, { params: { page: 1, page_size: 50 }, withCredentials: true }),
      axios.get(`${API}/admin/translations/stats`, { withCredentials: true })
    ]).then(([jobsRes, statsRes]) => { setJobs(jobsRes.data.jobs); setStats(statsRes.data); })
      .catch(() => toast.error("Failed to load translations"))
      .finally(() => setIsLoading(false));
  }, []);

  const statusColors = {
    completed: 'bg-emerald-500/20 text-emerald-400',
    failed: 'bg-red-500/20 text-red-400',
    pending: 'bg-gray-500/20 text-gray-400',
  };

  return (
    <AdminPageLayout user={user} onLogout={onLogout} activeTab="translations">
      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
      ) : (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-white">Translations</h1>
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[hsl(220,25%,10%)] rounded-xl p-4 border border-white/5">
                <p className="text-white/50 text-sm">Completed</p>
                <p className="text-2xl font-bold text-emerald-400">{stats.status_distribution?.completed || 0}</p>
              </div>
              <div className="bg-[hsl(220,25%,10%)] rounded-xl p-4 border border-white/5">
                <p className="text-white/50 text-sm">Failed</p>
                <p className="text-2xl font-bold text-red-400">{stats.status_distribution?.failed || 0}</p>
              </div>
              <div className="bg-[hsl(220,25%,10%)] rounded-xl p-4 border border-white/5">
                <p className="text-white/50 text-sm">Processing</p>
                <p className="text-2xl font-bold text-amber-400">{(stats.status_distribution?.extracting || 0) + (stats.status_distribution?.transcribing || 0) + (stats.status_distribution?.translating || 0)}</p>
              </div>
              <div className="bg-[hsl(220,25%,10%)] rounded-xl p-4 border border-white/5">
                <p className="text-white/50 text-sm">Pending</p>
                <p className="text-2xl font-bold text-gray-400">{stats.status_distribution?.pending || 0}</p>
              </div>
            </div>
          )}
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl border border-white/5 overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/50 font-medium">Job ID</th>
                <th className="text-left p-4 text-white/50 font-medium">User</th>
                <th className="text-left p-4 text-white/50 font-medium">Language</th>
                <th className="text-left p-4 text-white/50 font-medium">Status</th>
                <th className="text-left p-4 text-white/50 font-medium">Created</th>
              </tr></thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.job_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4"><span className="text-white font-mono text-sm">{job.job_id.slice(0,8)}...</span></td>
                    <td className="p-4"><p className="text-white text-sm">{job.user_name}</p><p className="text-white/50 text-xs">{job.user_email}</p></td>
                    <td className="p-4 text-white">{job.target_language}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${statusColors[job.status] || 'bg-white/10 text-white'}`}>{job.status}</span></td>
                    <td className="p-4 text-white/50 text-sm">{new Date(job.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

// Admin Waitlist Page
const AdminWaitlistPage = ({ user, onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [planBreakdown, setPlanBreakdown] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/waitlist`, { withCredentials: true })
      .then(res => { setEntries(res.data.entries); setPlanBreakdown(res.data.plan_breakdown); })
      .catch(() => toast.error("Failed to load waitlist"))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <AdminPageLayout user={user} onLogout={onLogout} activeTab="waitlist">
      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
      ) : (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-white">Waitlist ({entries.length})</h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(planBreakdown).map(([plan, count]) => (
              <div key={plan} className="bg-[hsl(220,25%,10%)] rounded-xl p-4 border border-white/5">
                <p className="text-white/50 text-sm capitalize">{plan}</p>
                <p className="text-2xl font-bold text-white">{count}</p>
              </div>
            ))}
          </div>
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl border border-white/5 overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/50 font-medium">Email</th>
                <th className="text-left p-4 text-white/50 font-medium">Name</th>
                <th className="text-left p-4 text-white/50 font-medium">Plan</th>
                <th className="text-left p-4 text-white/50 font-medium">Country</th>
                <th className="text-left p-4 text-white/50 font-medium">Date</th>
              </tr></thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4 text-white">{e.email}</td>
                    <td className="p-4 text-white">{e.full_name}</td>
                    <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 capitalize">{e.plan_id}</span></td>
                    <td className="p-4 text-white/70">{e.country || '-'}</td>
                    <td className="p-4 text-white/50 text-sm">{new Date(e.registered_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

// Admin System Page
const AdminSystemPage = ({ user, onLogout }) => {
  const [health, setHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/system/health`, { withCredentials: true })
      .then(res => setHealth(res.data))
      .catch(() => toast.error("Failed to load system health"))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <AdminPageLayout user={user} onLogout={onLogout} activeTab="system">
      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
      ) : (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-white">System Health</h1>
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl p-6 border border-white/5 flex items-center gap-4">
            <div className={`w-4 h-4 rounded-full ${health?.status === 'healthy' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            <span className={`text-xl font-semibold ${health?.status === 'healthy' ? 'text-emerald-400' : 'text-amber-400'}`}>
              System {health?.status === 'healthy' ? 'Healthy' : 'Degraded'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AdminMetricCard title="Storage Used" value={`${health?.database?.storage_size_mb || 0} MB`} icon={Server} color="cyan" />
            <AdminMetricCard title="Data Size" value={`${health?.database?.data_size_mb || 0} MB`} icon={FileAudio} color="purple" />
            <AdminMetricCard title="Active Jobs" value={health?.active_jobs || 0} icon={Zap} color="amber" />
          </div>
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl p-6 border border-white/5">
            <h3 className="text-lg font-semibold text-white mb-4">Collection Counts</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(health?.collection_counts || {}).map(([name, count]) => (
                <div key={name} className="p-3 rounded-lg bg-white/5">
                  <p className="text-white/50 text-sm">{name}</p>
                  <p className="text-xl font-bold text-white">{count}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[hsl(220,25%,10%)] rounded-2xl p-6 border border-white/5">
            <h3 className="text-lg font-semibold text-white mb-2">Recent Errors (1h)</h3>
            <p className={`text-2xl font-bold ${health?.recent_errors_1h > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{health?.recent_errors_1h || 0}</p>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

function AppRouter({ user, onLogout, onAcceptTerms }) {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  
  // Check if it's an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Admin routes - require admin role
  if (isAdminRoute) {
    // Check admin access
    if (!user?.admin_role) {
      return (
        <div className="min-h-screen bg-[hsl(220,30%,6%)] flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-white/50 mb-4">You do not have permission to access the admin panel.</p>
            <a href="/" className="text-cyan-400 hover:text-cyan-300">← Return to Home</a>
          </div>
        </div>
      );
    }
    
    return (
      <Routes>
        <Route path="/admin" element={<AdminDashboardPage user={user} onLogout={onLogout} />} />
        <Route path="/admin/users" element={<AdminUsersPage user={user} onLogout={onLogout} />} />
        <Route path="/admin/users/:userId" element={<AdminUserDetailsPage user={user} onLogout={onLogout} />} />
        <Route path="/admin/translations" element={<AdminTranslationsPage user={user} onLogout={onLogout} />} />
        <Route path="/admin/waitlist" element={<AdminWaitlistPage user={user} onLogout={onLogout} />} />
        <Route path="/admin/system" element={<AdminSystemPage user={user} onLogout={onLogout} />} />
      </Routes>
    );
  }
  
  return (
    <>
      {/* Don't show navbar on terms acceptance page */}
      {location.pathname !== '/accept-terms' && <Navbar user={user} onLogout={onLogout} />}
      <Routes>
        <Route path="/" element={<LandingPage user={user} />} />
        <Route path="/pricing" element={<PricingPage user={user} />} />
        <Route path="/player/:jobId" element={<PlayerPage user={user} />} />
        <Route path="/video/:jobId" element={<VideoPlayerPage user={user} />} />
        <Route path="/library" element={<LibraryPage user={user} />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/accept-terms" element={<TermsAcceptancePage user={user} onAcceptTerms={onAcceptTerms} />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
    </>
  );
}

// ==================== TERMS PAGE (Public) ====================
const TermsPage = () => {
  const [termsContent, setTermsContent] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/terms`)
      .then(res => setTermsContent(res.data.content))
      .catch(() => toast.error("Failed to load terms"));
  }, []);

  return (
    <div className="min-h-screen noise hero-gradient pt-32 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-3xl p-8"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Terms & Conditions</h1>
              <p className="text-white/50">Effective: February 2, 2026</p>
            </div>
          </div>
          
          <div className="prose prose-invert prose-sm max-w-none mb-8">
            <pre className="whitespace-pre-wrap font-sans text-sm text-white/80 leading-relaxed">
              {termsContent || "Loading..."}
            </pre>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="btn-secondary px-6 py-2 rounded-full"
          >
            Go Back
          </button>
        </motion.div>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  // Callback when user accepts terms
  const handleAcceptTerms = () => {
    // Refresh user data to update terms_accepted status
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => {});
  };

  if (isLoading) return <div className="min-h-screen bg-[hsl(220,30%,6%)] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-amber-200/60" /></div>;

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <BrowserRouter>
        <Toaster position="top-center" toastOptions={{ style: { background: 'hsl(220, 25%, 12%)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' } }} />
        <AppRouter user={user} onLogout={handleLogout} onAcceptTerms={handleAcceptTerms} />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
